import { environment, getOnlineServices, skipValidation } from "../util";
import { indexDBStorage } from "../storage/indexDBStorage";
import RequestService from "../util/request";
import {ValidationResponse, ValidationResponseCheck, ValidationSummary} from '@npd-ng/adp-types';
import { useDispatch, useSelector } from "react-redux";
import {  createFailureAction, createSetConfirmationModalAction, createSetDeviceReadinessSummary, createSetViewAction } from "state/reducer";
import TimeService from "../services/timeService";
import { useRedirectionService } from "./useRedirectionService";
import { IADPState, OSThreshold, DeviceReadinessInfo, AppConfigExtended, FailureType } from "../Types/AppStateTypes";
import { useUpdatesService } from "./useUpdatesService";
import { Manifest } from "../Types/ServiceTypes";
import { DeviceMetricsService } from "../services/deviceMetricsService";
const logPrefix = "[ADPClient][DeviceValdation]";
export type checkStatus = {
  actionOnFailure: FailureType | "DeviceValidationFailure",
  isClassroomStressTestPassed?: boolean,
  isCheckPassed: boolean,
  messageOnFailure?:string
}

export function useDeviceValidationService() {
  const dispatch = useDispatch()
  const state = useSelector((state:{rootState:IADPState})=>state.rootState)
  const batteryState = useSelector((state:{rootState:IADPState})=>state.rootState.deviceState.batteryState)
  const DeviceValidation = useSelector((state:{rootState:IADPState})=>state.rootState.appState?.configuration?.DeviceValidation)
  const {FetchReleaseManifest} = useUpdatesService();
  const {redirectToSCI} = useRedirectionService()
  const performCPUCheck= async (value:string)=>{
    const cpuValue = JSON.parse(value);
    return new Promise<ValidationResponse & checkStatus >((resolve) => {
     const CPUCheck: ValidationResponse & checkStatus = {
      name: DeviceValidation?.checkCPU.name || "checkCPU",
      threshold: DeviceValidation?.checkCPU.threshold.toLocaleString() || "70",
      time: TimeService.current(),
      type:DeviceValidation?.checkCPU.type || ValidationResponseCheck.SOFT_CHECK,
      message: DeviceValidation?.checkCPU.threshold < cpuValue ? "Warning" : "Success",
      value:value,
      isCheckPassed: DeviceValidation?.checkCPU.threshold > cpuValue,
      actionOnFailure: "FailedHardwareCheck"
    }
    resolve(CPUCheck);
  })
  }
  const performMemoryCheck= async (value:string)=>{
    //@ts-ignore
    const memoryValue = JSON.parse(value);
    return new Promise<ValidationResponse & checkStatus >((resolve) => {
      const memoryCheck: ValidationResponse & checkStatus = {
       name: DeviceValidation?.checkMemory.name || "checkMemory",
       threshold: DeviceValidation?.checkMemory.threshold.toLocaleString() || "4000000000",
       time: TimeService.current(),
       type:DeviceValidation?.checkMemory.type || ValidationResponseCheck.HARD_CHECK,
       message: memoryValue.total < DeviceValidation?.checkMemory.threshold ? "Warning" : "Success",
       value:value,
       isCheckPassed: memoryValue.total > DeviceValidation?.checkMemory.threshold,
       actionOnFailure: "FailedHardwareCheck",
       messageOnFailure: state.currentLanguageContent?.deviceValidationFailure?.checkMemory
     }
    resolve(memoryCheck);
  })
  }
  const performStorageCheck= async (value:string)=>{
    //@ts-ignore
    const storageValue = JSON.parse(value);
    return new Promise<ValidationResponse & checkStatus >((resolve) => {
      const storageCheck: ValidationResponse & checkStatus = {
       name: DeviceValidation?.checkStorage.name || "checkStorage",
       threshold: DeviceValidation?.checkStorage.threshold.toLocaleString() || "2000000000",
       time: TimeService.current(),
       type:DeviceValidation?.checkStorage.type || ValidationResponseCheck.HARD_CHECK,
       message: storageValue.FreeSpace < DeviceValidation?.checkStorage.threshold ? "Warning" : "Success",
       value:value,
       isCheckPassed: storageValue.FreeSpace > DeviceValidation?.checkStorage.threshold,
       actionOnFailure: "FailedHardwareCheck",
       messageOnFailure: state.currentLanguageContent?.deviceValidationFailure?.checkStorage
     }
    resolve(storageCheck);
  })
  }
  const performProccessorCheck= async (value:string)=>{
    const proccessorValue = JSON.parse(value);
    const ghzValue = proccessorValue.MaxClockSpeed/1000
    return new Promise<ValidationResponse & checkStatus >((resolve) => {
      const proccessorCheck: ValidationResponse & checkStatus = {
       name: DeviceValidation?.checkProccessor.name || "checkProccessor",
       threshold: DeviceValidation?.checkProccessor.threshold.toLocaleString() || "1",
       time: TimeService.current(),
       type:DeviceValidation?.checkProccessor.type || ValidationResponseCheck.HARD_CHECK,
       message: ghzValue < DeviceValidation?.checkProccessor.threshold ? "Warning" : "Success",
       value:`${value} ghz value: ${ghzValue.toLocaleString()}`,
       isCheckPassed: ghzValue > DeviceValidation?.checkProccessor.threshold,
       actionOnFailure: "FailedHardwareCheck",
       messageOnFailure: state.currentLanguageContent?.deviceValidationFailure?.checkProccessor
     }
    resolve(proccessorCheck);
  })
  }
  const performBrightnessCheck= async (value:string)=>{
    return new Promise<ValidationResponse & checkStatus >((resolve) => {
      const brightnessCheck: ValidationResponse & checkStatus = {
       name: DeviceValidation?.checkBrightness.name || "checkBrightness",
       threshold: DeviceValidation?.checkBrightness.threshold.toLocaleString() || "NotApplicable",
       time: TimeService.current(),
       type:DeviceValidation?.checkBrightness.type || ValidationResponseCheck.HARD_CHECK,
       message:"Success",
       value:value,
       isCheckPassed: true,//there are no cases in which this check fails as it is only for information purposes
       actionOnFailure: "FailedHardwareCheck",
     }
    resolve(brightnessCheck);
  })
  }
  const performHardwareCheck= async (value:string)=>{
    return new Promise<ValidationResponse & checkStatus >((resolve) => {
      const hardwareCheck: ValidationResponse & checkStatus = {
       name: DeviceValidation?.checkHardware.name || "checkHardware",
       threshold: DeviceValidation?.checkHardware.threshold.toLocaleString() || "NotApplicable",
       time: TimeService.current(),
       type:DeviceValidation?.checkHardware.type || ValidationResponseCheck.HARD_CHECK,
       message:"Success",
       value:value,
       isCheckPassed: true,//there are no cases in which this check fails as it is only for information purposes
       actionOnFailure: "FailedHardwareCheck"
     }
    resolve(hardwareCheck);
  })
  }
 const performVersionCheck=()=>{//TODO see if you can use the same implementation for npn mode
  console.log(logPrefix, "performVersionCheck");
  return new Promise<ValidationResponse & checkStatus>((resolve) => {
    const versionCheck: ValidationResponse & checkStatus  = {
      name: DeviceValidation?.checkVersion.name || "checkVersion",
      threshold: "",
      time: TimeService.current(),
      type:DeviceValidation?.checkVersion.type || ValidationResponseCheck.HARD_CHECK,
      message:"",
      value:state.appState.releaseVersion || "NotFound",
      isCheckPassed: false,
      actionOnFailure: "FailedVersionCheck",
   }
    const onResponse=(response:any)=>{
      const manifest: Manifest = response;
      let versionResponse={...versionCheck}
      if(!response || response?.status!==200){
        versionResponse.message =  "Failed"
        versionResponse.isCheckPassed = false;
        versionResponse.value = response?.status;
        versionResponse.actionOnFailure= "NoServerConnectionDetected";
        versionResponse.threshold = "Server release version is not available";
        resolve(versionResponse);
        return;
      }
      else if(manifest.infoCode=== "INFO-6001" && manifest.manifest?.product?.version !== state.appState.releaseVersion){
        versionResponse.message = "Mismatch";
        versionResponse.threshold = manifest.manifest?.product?.version
        versionResponse.value = state.appState.releaseVersion || "NotFound";
        versionResponse.isCheckPassed = false
      }
      else if(manifest.infoCode=== "INFO-6001" && manifest.manifest?.product?.version === state.appState.releaseVersion){
        versionResponse.message =  "Match";
        versionResponse.threshold = manifest.manifest?.product?.version
        versionResponse.value = state.appState.releaseVersion || "NotAvailable";
        versionResponse.isCheckPassed = true
      }
      else {
        versionResponse.message =  "Match";
        versionResponse.threshold = state.appState.releaseVersion || "NotFound";
        versionResponse.value = state.appState.releaseVersion || "NotFound";
        versionResponse.isCheckPassed = true;
      }
      resolve(versionResponse);
    }
      FetchReleaseManifest(onResponse).catch((e:any)=>{
        versionCheck.message =  "Warning"
        versionCheck.value = JSON.stringify(e); 
        versionCheck.isCheckPassed = true;
        resolve(versionCheck);
      });
      versionCheck.message =  "Skipped";
      versionCheck.threshold = state.appState.releaseVersion || "NotFound";
      versionCheck.value = state.appState.releaseVersion || "NotFound";
      versionCheck.isCheckPassed = true;
      resolve(versionCheck);
  })
  
 }

 const performHealthCheck=()=>{
  console.log(logPrefix, "performHealthCheck");
  return new Promise<ValidationResponse & checkStatus>((resolve) => {
    const helathCheck = DeviceValidation?.checkCloudServerHealth//TODO implement npn mode state.sessionState.selectedAssessmentMode === "ncs" ? DeviceValidation?.checkNCSServerHealth : DeviceValidation?.checkCloudServerHealth
  const healthCheck: ValidationResponse & checkStatus = {
    name: helathCheck?.name || "performHealthCheck",
    threshold: helathCheck?.threshold,
    time: TimeService.current(),
    type:helathCheck?.type || ValidationResponseCheck.HARD_CHECK,
    message:"",
    value:"",
    isCheckPassed: false,
    actionOnFailure: "NoServerConnectionDetected",
 }
  let url = `${environment.resolveApiDomain(state.appState.configuration)}/${getOnlineServices(state.appState.configuration)?.cloudServiceHealth}`;
  /*if(state.sessionState.selectedAssessmentMode === "ncs" && state.connectedNCSState){
    url = `https://${state.connectedNCSState?.connectionInfo.ipAddress}:${state.connectedNCSState?.config.adsServices.port}/session-service/health`;
  } TODO implement npn mode */
  console.info("The reponse of the health check",url);
  fetch(url).then(async (response)=>{
    healthCheck.message = await response.json()
    healthCheck.value = response?.status.toLocaleString();
    console.info("The reponse of the health check",healthCheck.message);
    response?.status === 200 ? healthCheck.isCheckPassed = true: healthCheck.isCheckPassed = false;
  }).catch((e)=>{
    healthCheck.message = "Failed"
    healthCheck.value = JSON.stringify(e);
    healthCheck.isCheckPassed = false;
  }).finally(()=>{
    resolve(healthCheck)
  })
})
 }
/*const performEnvCheck=(environment:string)=>{ TODO when we split online ot npn see if we can split this file into two with this in the npn mode file
 console.log(logPrefix, "performEnvCheck");
 return new Promise<ValidationResponse & checkStatus>((resolve) => { 
 const envCheck: ValidationResponse & checkStatus = {
   name: DeviceValidation?.checkEnv?.name || "checkEnv",
   threshold: environment || DeviceValidation?.checkEnv?.threshold,
   time: TimeService.current(),
   type:DeviceValidation?.checkEnv?.type || ValidationResponseCheck.HARD_CHECK,
   message:"",
   value:"",
   isCheckPassed: false,
   actionOnFailure: "SystemError",
}
   if(state.sessionState.selectedAssessmentMode!== "ncs"){
     envCheck.message = "Skipped";
     envCheck.value = `online mode selected skipping check.`;
     envCheck.isCheckPassed = true;
   }
   else if(environment !== state.appState.configuration?.environment) {
     console.log("The environment is set to:",environment)
     envCheck.message = "Failed";
     envCheck.value = `NCS Environment ${environment} does not match student app ${state.appState.configuration?.environment}`;
     envCheck.isCheckPassed = false;
   }else{
    envCheck.isCheckPassed = true
    envCheck.message = "Success";
    envCheck.value= state.appState.configuration?.environment || "NotAvailable"
   }
   resolve(envCheck)
  })
}*/
const performScreenResolutionCheck=()=>{
  console.log(logPrefix, "performScreenResolutionCheck");
  return new Promise<ValidationResponse & checkStatus>((resolve) => {
  const resolutionCheck: ValidationResponse & checkStatus = {
    name: DeviceValidation?.checkScreenResolution?.name || "checkScreenResolution",
    threshold: DeviceValidation?.checkScreenResolution?.threshold,
    time: TimeService.current(),
    type:DeviceValidation?.checkScreenResolution?.type || ValidationResponseCheck.HARD_CHECK,
    message:"",
    value:"",
    isCheckPassed: false,
    actionOnFailure: "DeviceValidationFailure",
    messageOnFailure: state.currentLanguageContent?.deviceValidationFailure?.checkScreenResolution
 }
    const thresholdList = resolutionCheck?.threshold?.split(",");
    
    if(thresholdList.length >1){
      let height = window?.screen?.height;
      let width = window?.screen?.width;
      if(width >= Number.parseInt(thresholdList[0]) && height >= Number.parseInt(thresholdList[1])){
        resolutionCheck.message = "Success"
        resolutionCheck.isCheckPassed = true;
        resolutionCheck.value = `${JSON.stringify({displayResolution: `${width},${height}`,scale:window.devicePixelRatio,layoutViewport: `${window.innerWidth},${window.innerHeight}`})}` ;
      } 
      else{
        height = thresholdList[2] && window.devicePixelRatio ? 
        window?.screen?.height * window.devicePixelRatio : window?.screen?.height;
        width =  thresholdList[2] && window.devicePixelRatio ? 
        window?.screen?.width * window.devicePixelRatio : window?.screen?.width;
        resolutionCheck.message = "Failed"
        resolutionCheck.isCheckPassed = false;
        if(width >= Number.parseInt(thresholdList[0]) && height >= Number.parseInt(thresholdList[1])){
          resolutionCheck.value = `Screen resolution failed due to invalid DPR: ${JSON.stringify({displayResolution: `${width},${height}`,scale:window.devicePixelRatio,layoutViewport: `${window.innerWidth},${window.innerHeight}`})}` ;
        }
        else{
          resolutionCheck.value = `Screen resolution failed due to invalid minimum screen resolution: ${JSON.stringify({displayResolution: `${width},${height}`,scale:window.devicePixelRatio,layoutViewport: `${window.innerWidth},${window.innerHeight}`})}` ; 
        }
      }
    }
    resolve(resolutionCheck)
 })
}

const performScreenCheck=()=>{
  console.log(logPrefix, "performScreenCheck");
  return new Promise<ValidationResponse & checkStatus>((resolve) => {
    const screen = {
      availHeight:window.screen?.availHeight,
      availWidth: window.screen?.availWidth,// @ts-ignore
      availLeft:window.screen?.availLeft,// @ts-ignore
      availTop: window.screen?.availTop,
      colorDepth: window.screen?.colorDepth,
      height: window.screen?.height,// @ts-ignore
      isExtended: window.screen?.isExtended,
      orientation: {angle: window.screen?.orientation?.angle, type: window.screen?.orientation.type},
      pixelDepth: window.screen?.pixelDepth,
      width: window.screen?.width,
      innerWidth: window.innerWidth,
      innerHeight: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio
    }
    console.log(screen);
  const resolutionCheck: ValidationResponse & checkStatus = {
    name: DeviceValidation?.checkScreen?.name || "checkScreen",
    threshold: DeviceValidation?.checkScreen?.threshold,
    time: TimeService.current(),
    type:DeviceValidation?.checkScreen?.type || ValidationResponseCheck.SOFT_CHECK,
    message:"Success",// @ts-ignore
    value:screen,
    isCheckPassed: true,
    actionOnFailure: "DeviceValidationFailure",
 }
    resolve(resolutionCheck)
 })
}
const performDeviceCheck=()=>{
  console.log(logPrefix, "performDeviceCheck");
  return new Promise<ValidationResponse & checkStatus>((resolve) => {
  const deviceCheck: ValidationResponse & checkStatus = {
    name: DeviceValidation?.checkDevice?.name || "checkDevice",
    threshold: DeviceValidation?.checkDevice?.threshold,
    time: TimeService.current(),
    type:DeviceValidation?.checkDevice?.type || ValidationResponseCheck.SOFT_CHECK,
    message:"Success",// @ts-ignore
    value:state.deviceState.systemInfo,
    isCheckPassed: true,
    actionOnFailure: "DeviceValidationFailure",
 }
    resolve(deviceCheck)
 })
}
const performDuplicateScreenCheck=()=>{
  console.log(logPrefix, "performDuplicateScreenCheck");
  return new Promise<ValidationResponse & checkStatus>((resolve) => {
  const DuplicateScreenCheck: ValidationResponse & checkStatus = {
    name: DeviceValidation?.checkDuplicateScreen?.name || "checkDuplicateScreen",
    threshold: DeviceValidation?.checkDuplicateScreen?.threshold,
    time: TimeService.current(),
    type:DeviceValidation?.checkDuplicateScreen?.type || ValidationResponseCheck.HARD_CHECK,
    message:"",
    value:"",
    isCheckPassed: false,
    actionOnFailure: "DeviceValidationFailure",
    messageOnFailure: state.currentLanguageContent?.deviceValidationFailure?.checkDuplicateScreen
 }
    const duplicateScreens = (window.screen as any).isExtended;//The any conversion is because the typing is wrong and doesn't beleve that the .isExtended method exists.
    DuplicateScreenCheck.value = duplicateScreens;
    if(DeviceValidation?.checkDuplicateScreen?.threshold as boolean === duplicateScreens.toString()){
      DuplicateScreenCheck.message = "Success"
      DuplicateScreenCheck.isCheckPassed = true;
      }
      else{
        DuplicateScreenCheck.message = "Failed"
        DuplicateScreenCheck.isCheckPassed = false;
      }
    resolve(DuplicateScreenCheck)
 })
}
const performChromeExtensionCheck=()=>{
  console.log(logPrefix, "performChromeExtensionCheck");
  return new Promise<ValidationResponse & checkStatus>((resolve) => {
  const ChromeExtension: ValidationResponse & checkStatus = {
    name: DeviceValidation?.checkChromeExtension?.name || "checkChromeExtension",
    threshold: DeviceValidation?.checkChromeExtension?.threshold,
    time: TimeService.current(),
    type:DeviceValidation?.checkChromeExtension?.type || ValidationResponseCheck.HARD_CHECK,
    message:"",
    value:"",
    isCheckPassed: false,
    actionOnFailure: "DeviceValidationFailure",
    messageOnFailure: state.currentLanguageContent?.deviceValidationFailure?.checkChromeExtension
 }
    const chromeExtension = state.updateState.appComponentVersions?.find((element) => element.name === "ADP-EXTENSION");
    ChromeExtension.value = chromeExtension?.version || "NotAvailable";
    if(chromeExtension){
      ChromeExtension.message = "Success"
      ChromeExtension.isCheckPassed = true;
      }
      else{
        ChromeExtension.message = "Failed"
        ChromeExtension.isCheckPassed = false;
      }
    resolve(ChromeExtension)
 })
}
const performTouchDeviceCheck=()=>{
  console.log(logPrefix, "performTouchDeviceCheck");
  return new Promise<ValidationResponse & checkStatus>((resolve) => {
  const TouchDeviceCheck: ValidationResponse & checkStatus = {
    name: DeviceValidation?.checkTouchDevice?.name || "checkTouchDevice",
    threshold: DeviceValidation?.checkTouchDevice?.threshold,
    time: TimeService.current(),
    type:DeviceValidation?.checkTouchDevice?.type || ValidationResponseCheck.SOFT_CHECK,
    message:`device has maxTouchPoints ${navigator.maxTouchPoints}` ,
    value: navigator.maxTouchPoints > 0 ? "touch supported": "touch not supported",
    isCheckPassed: navigator.maxTouchPoints > 0,
    actionOnFailure: "DeviceValidationFailure",
    messageOnFailure: state.currentLanguageContent?.deviceValidationFailure?.checkTouchScreen
 }
    resolve(TouchDeviceCheck)
 })
}
const performOSVersionCheck=()=>{
  console.log(logPrefix, "performOSVersionCheck");
  return new Promise<ValidationResponse & checkStatus>((resolve) => {
  const OSVersionCheck: ValidationResponse & checkStatus = {
    name: DeviceValidation?.checkOSVersion?.name || "checkOSVersion",
    threshold: DeviceValidation?.checkOSVersion?.threshold,
    time: TimeService.current(),
    type:DeviceValidation?.checkOSVersion?.type || ValidationResponseCheck.HARD_CHECK,
    message:"Failed",
    value:"NotAvailable",
    isCheckPassed: false,
    actionOnFailure: "DeviceValidationFailure",
    messageOnFailure: state.currentLanguageContent?.deviceValidationFailure?.checkOSVersion
 }
    const osList:OSThreshold[] =JSON.parse(DeviceValidation?.checkOSVersion?.threshold)
    const userAgent = window.navigator.userAgent;
    const osRegexString = "\\(.*?\\)"
    const chromeRegexString = "Chrome.*?\\s"
    const versionRegexString = "(\\d)*\\.(\\d)*"
    const versionRegex = new RegExp(versionRegexString);
    const chromeRegex = RegExp(chromeRegexString);
    const chromeInformation = chromeRegex.exec(userAgent);
    const osRegex = RegExp(osRegexString);
    const osInformation = osRegex.exec(userAgent);
    console.log("The os version information is:",userAgent,chromeInformation,osInformation)
    console.log(JSON.stringify(osList));
    if(osInformation && osInformation?.length>0){
      osList?.forEach((os)=>{
        if(osInformation[0].includes(os.os)){
          if(os.checkOs){
            OSVersionCheck.value = osInformation[0];
            const osVersion = versionRegex.exec(osInformation[0])
            if(osVersion && osVersion.length>0){
              if(Number(osVersion[0].split(".")[0]) >= Number(os?.version)){
                OSVersionCheck.isCheckPassed = true;
                OSVersionCheck.message = "Success"
              }
            }
            OSVersionCheck.value = osInformation[0];
          }
          else{
            if(chromeInformation && chromeInformation.length>0){
              OSVersionCheck.value = osInformation[0];
              const chromeVersion = versionRegex.exec(chromeInformation[0]);
              if(chromeVersion && chromeVersion.length>0){
                if(Number(chromeVersion[0].split(".")[0]) >= Number(os?.version))
                {
                OSVersionCheck.isCheckPassed = true;
                OSVersionCheck.message = "Success"
                }
              }
              OSVersionCheck.value = chromeInformation[0];
            }
          }

        }
      })
      if(OSVersionCheck.value === "NotAvailable"){
        OSVersionCheck.isCheckPassed = false;
        OSVersionCheck.message = "Failed"
      }
    }
    resolve(OSVersionCheck)
 })
}
 const performSystemClockCheck=()=>{
  console.log(logPrefix, "performSystemClockCheck");
  return new Promise<ValidationResponse & checkStatus >(async (resolve) => {
    const systemClockCheck: ValidationResponse & checkStatus = {
    name: DeviceValidation?.checkSystemClock.name || "performSystemClockCheck",
    threshold: DeviceValidation?.checkSystemClock.threshold.toString(),
    time: TimeService.current(),
    type:DeviceValidation?.checkSystemClock.type || ValidationResponseCheck.HARD_CHECK, //TODO impleemnt npn mode state.sessionState.selectedAssessmentMode === "ncs" ? ValidationResponseCheck.SOFT_CHECK : DeviceValidation?.checkSystemClock.type || ValidationResponseCheck.HARD_CHECK,
    message:"",
    value:"",
    isCheckPassed: false,
    actionOnFailure: "FailedSystemTimeCheck"
  };
  let url = `${environment.resolveApiDomain(state.appState.configuration)}/${getOnlineServices(state.appState.configuration)?.cloudServiceHealth}`;
  /*if(state.sessionState.selectedAssessmentMode === "ncs" && state.connectedNCSState){
    url = `https://${state.connectedNCSState?.connectionInfo.ipAddress}:${state.connectedNCSState?.config.adsServices.port}/session-service/health`;
  }TODO implement npn mode*/
  const dateFromResponse = await TimeService.validateCurrentTime(url,DeviceValidation?.checkSystemClock.threshold)
    if(dateFromResponse){
       console.log(logPrefix,'server time', dateFromResponse.serverTime, "local time", dateFromResponse.localTime);
       systemClockCheck.message = dateFromResponse.passed ? "Success" : "Failed";
       systemClockCheck.threshold = dateFromResponse.serverTime
       systemClockCheck.value = dateFromResponse.localTime
       systemClockCheck.isCheckPassed = dateFromResponse.passed;
         }
    else {
      console.warn(`checkSystemClock unable to get date from cloud services: (${url})`);
      systemClockCheck.message = "Warning";
      systemClockCheck.value = `checkSystemClock unable to get date from cloud services: (${url}. Date property is missing)`
      systemClockCheck.isCheckPassed = false;
    }
    resolve(systemClockCheck);
  });
 
 }
 const performBatteryCheck= async ()=>{
  return new Promise<ValidationResponse & checkStatus >((resolve) => {
   const batteryCheck: ValidationResponse & checkStatus = {
    name: DeviceValidation?.checkBattery.name || "checkBattery",
    threshold: DeviceValidation?.checkBattery.threshold.toLocaleString() || "0.5",
    time: TimeService.current(),
    type:DeviceValidation?.checkBattery.type || ValidationResponseCheck.HARD_CHECK,
    message:(batteryState?.level || 0) * 100 < DeviceValidation?.checkBattery.threshold ? "Warning" : "Success",
    value:JSON.stringify({batteryLevel: batteryState?.level, isCharging: batteryState?.charging}),
    isCheckPassed: true,
    actionOnFailure: "SystemError"
  }
  resolve(batteryCheck);
})
}
const _postDeviceMetrics= async (failureReasons?: any[])=>{
  const {postDeviceMetrics} = DeviceMetricsService()
  const deviceValidationStatus: DeviceReadinessInfo={
    "readinessCheckStartedAt": state.deviceReadinessState.deviceReadinessInfo?.readinessCheckStartedAt || new Date().toISOString(),
    "deviceReadinessCheckPassed": (!failureReasons || failureReasons.length === 0) ? true  : false ,          
    "state": "ReadinessCheckCompleted",
    "failureReason": failureReasons || [],
    "readinessCheckCompletedAt": new Date().toISOString()     
  };
  //udpate state first prior posting the device metrics API to avoid race condition
  //dispatch(createUpdateDeviceValidationStateAction(deviceValidationStatus));
  await postDeviceMetrics(state,deviceValidationStatus,undefined);
  return;
}
const _postDeviceMetricsInProgress= async ()=>{
  console.log("[test] inside the device metrics")
  const {postDeviceMetrics} = DeviceMetricsService()
  const deviceValidationStatus: DeviceReadinessInfo={
    "readinessCheckStartedAt": new Date().toISOString(),
    "deviceReadinessCheckPassed": false ,          
    "state": "ReadinessCheckInProgress",
    "failureReason":  [],
    "readinessCheckCompletedAt": ""     
  };
  postDeviceMetrics(state,deviceValidationStatus);
  //dispatch(createUpdateDeviceValidationStateAction(deviceValidationStatus));
  return;
}
 const postAllValidationLogsFromIndexDB= async ()=>{
  const dbInstance = await indexDBStorage.getInstance();
  const keys = await dbInstance.transactions.getAll("adp-device-validation-indexed");
  console.log("postAllValidationLogsFromIndexDB",keys);
  
  await Promise.all(keys.map(async (key:IDBValidKey) => {
    const data = await dbInstance.transactions.get("adp-device-validation-indexed",key as string);
    if(keys?.length > 0  && !state.appState.appLaunchId?.includes(key as string)){
      const fileName = `${key}.device-validation.json`
      const {payload,logHeader} = RequestService.constructDeviceValidationDataTransferPayload(state,fileName);
      let status = undefined as any
      if(data?.deviceReadinessChecks){
        status= await RequestService.postValidationLogs(state,fileName,{logHeader:data?.logHeader ||logHeader,payload:payload},data.deviceReadinessChecks);
      }
      else{
        status= await RequestService.postValidationLogs(state,fileName,{logHeader:logHeader,payload:payload},data);
      }
        if(status){
          console.log(`successfully sent ${fileName} from the indexdb store. Deleting record form the indexdb.`)
          dbInstance.transactions.delete("adp-device-validation-indexed",key as string);
        }
    }
    }));
  return;
} 
const getSummary = (results:ValidationResponse[]) =>{
  const {logHeader} = RequestService.constructDeviceValidationDataTransferPayload(state,"");
  const failures = results.find(result => result.isCheckPassed === false && result.type === ValidationResponseCheck.HARD_CHECK);
  const deviceReadinessChecks:ValidationSummary = {
    ...logHeader,
    deviceName:state?.deviceState?.deviceId || "NotAvailable",
    status:failures ? "Failure" : "Success",
    responses:results
  }
  return deviceReadinessChecks;
}
 const postDeviceValidationLog = async(results:ValidationResponse[])=>{
  if(!state.appState.appLaunchId)return;
  const fileName = `${state.appState.appLaunchId}.${state.sessionState.selectedAssessmentMode}.device-validation.json`; 
  const {logHeader,payload} = RequestService.constructDeviceValidationDataTransferPayload(state,fileName);
  if(window.navigator.onLine){
    const response = await RequestService.postValidationLogs(state,fileName,{logHeader,payload},results);
    if(!response){
      //if posting failed store in indexDB for later re-posting
      if(state.appState.appLaunchId)indexDBStorage.storeValidationLogs({ logHeader:logHeader,deviceValidationChecks:getSummary(results)},state.appState.appLaunchId);
    };
    //post stored logs af any
    await postAllValidationLogsFromIndexDB();
  }
  else {
    if(state.appState.appLaunchId)indexDBStorage.storeValidationLogs({ logHeader:logHeader,deviceValidationChecks:getSummary(results)},state.appState.appLaunchId);
  }
 };
 const postLocationDeviceMetrics = async()=>{
  const {postDeviceMetrics} = DeviceMetricsService()
    const deviceValidation: DeviceReadinessInfo={
      "readinessCheckStartedAt": state.deviceReadinessState.deviceReadinessInfo?.readinessCheckStartedAt as string,
      "deviceReadinessCheckPassed": state.deviceReadinessState.deviceReadinessInfo?.state.includes("ReadinessCheckCompleted") || false,          
      "state": state.deviceReadinessState.deviceReadinessInfo?.state || "ReadinessCheckInProgress",
      "failureReason": state.deviceReadinessState?.deviceReadinessInfo?.failureReason || [],
      "readinessCheckCompletedAt": new Date().toISOString()   
    };
    await postDeviceMetrics(state,deviceValidation,state?.sessionState?.selectedAssessmentLocation);
    //dispatch(createSetViewAction("DeviceValidationView"));

  return;
}
const validate = (mode:'onlineChecks' | 'npnChecks')=>{
  const validationList = state?.appState?.configuration?.DeviceValidation[mode].split(',');
  console.log("Validating these checks:",validationList)
  const validationResults = new Array<Promise<ValidationResponse & checkStatus>>();
  validationList?.forEach((validationName)=>{
    switch(validationName){
      case "checkBattery":{
        try{
          validationResults.push(performBatteryCheck());
          break;
        }
        catch(err){
          console.error("[DeviceValidation] An error occured trying to get battery status.")
          break;
        }
      }
      case "checkScreen":{
        try{
          validationResults.push(performScreenCheck());
          break;
        }
        catch(err){
          console.error("[DeviceValidation] An error occured trying to get battery status.")
          break;
        }
      }
      case "checkDevice":{
        try{
          validationResults.push(performDeviceCheck());
          break;
        }
        catch(err){
          console.error("[DeviceValidation] An error occured trying to get battery status.")
          break;
        }
      }
      case "checkCloudServerHealth":{
        try{
          validationResults.push(performHealthCheck());
          break;
        }
        catch(err){
          console.error("[DeviceValidation] An error occured trying to get NAEP Server status.")
          break;
        }
      }
      case "checkSystemClock":{
        try{
          validationResults.push(performSystemClockCheck());
          break;
        }
        catch(err){
          console.error("[DeviceValidation] An error occured trying to get the current time.")
          break;
        }
      }
      case "checkScreenResolution":{
        try{
          validationResults.push(performScreenResolutionCheck());
          break;
        }
        catch(err){
          console.error("[DeviceValidation] An error occured trying to get Screen Resolution.")
          break;
        }
      }
      case "checkChromeExtension":{
        try{
          validationResults.push(performChromeExtensionCheck());
          break;
        }
        catch(err){
          console.error("[DeviceValidation] An error occured trying to get Chrome extension.")
          break;
        }
      }
      case "checkOSVersion":{
        try{
          validationResults.push(performOSVersionCheck());
          break;
        }
        catch(err){
          console.error("[DeviceValidation] An error occured trying to get Operating System information.")
          break;
        }
      }
      case "checkDuplicateScreen":{
        try{
          validationResults.push(performDuplicateScreenCheck());
          break;
        }
        catch(err){
          console.error("[DeviceValidation] An error occured trying to get the screen information.")
          break;
        }
      }
      case "checkTouchDevice":{
        try{
          validationResults.push(performTouchDeviceCheck());
          break;
        }
        catch(err){
          console.error("[DeviceValidation] An error occured trying to get the touchscreen information.")
          break;
        }
      }
      // case "checkNCSVersion":{
      //   try{
      //     validationResults.push(performNCSVersionCheck());
      //     break;
      //   }
      //   catch(err){
      //     console.error("[DeviceValidation] An error occured trying to get the version in npn mode.")
      //     break;
      //   }
      // }
      case "checkVersion":{
        try{
          validationResults.push(performVersionCheck());
          break;
        }
        catch(err){
          console.error("[DeviceValidation] An error occured trying to get the version in npn mode.")
          break;
        }
      }
      default:{
        break;
      }
    }
  })
  return validationResults
}
const validateVitalsChecks = ()=>{
  
  console.log("Validating these vitals checks:",state.deviceReadinessState.deviceVitalsInfo)
  const validationResults = new Array<Promise<ValidationResponse & checkStatus>>();
  state.deviceReadinessState.deviceVitalsInfo?.forEach(async (validation)=>{
    let validationName = validation.name
    switch(validationName){
      case "checkCPU":{
        try{
          validationResults.push(performCPUCheck(validation.value));
          break;
        }
        catch(err){
          console.error("[DeviceValidation] An error occured trying to get cpu status.")
          break;
        }
      }
      case "checkMemory":{
        try{
          validationResults.push(performMemoryCheck(validation.value));
          break;
        }
        catch(err){
          console.error("[DeviceValidation] An error occured trying to get memory status.")
          break;
        }
      }
      case "checkStorage":{
        try{
          validationResults.push(performStorageCheck(validation.value));
          break;
        }
        catch(err){
          console.error("[DeviceValidation] An error occured trying to get storage status.")
          break;
        }
      }
      case "checkProccessor":{
        try{
          validationResults.push(performProccessorCheck(validation.value));
          break;
        }
        catch(err){
          console.error("[DeviceValidation] An error occured trying to get proccessor status.")
          break;
        }
      }
      case "checkHardware":{
        try{
          validationResults.push(performHardwareCheck(validation.value));
          break;
        }
        catch(err){
          console.error("[DeviceValidation] An error occured trying to get hardware status.")
          break;
        }
      }
      case "checkBrightness":{
        try{
          validationResults.push(performBrightnessCheck(validation.value));
          break;
        }
        catch(err){
          console.error("[DeviceValidation] An error occured trying to get brightness status.")
          break;
        }
      }
    }
  })
  return validationResults
}
 const  performDeviceValidation= async (mode: 'online' | 'npn')=>{
  try{
    _postDeviceMetricsInProgress();
  }
  catch(err){
    console.error("[DeviceMetrics] sending the device metrics failed.")
  }
  return new Promise<boolean>(async (resolve) => {
    
    Promise.all([...validateVitalsChecks(),...validate(mode === 'online'? 'onlineChecks': 'npnChecks')]).then(async (results)=>{
      console.log("[DeviceValidation] the results of the device validation is: ",results)
      dispatch(createSetDeviceReadinessSummary(results));
      await postDeviceValidationLog(results);
      const failedChecks = results.filter(result => !result.isCheckPassed && result.type === ValidationResponseCheck.HARD_CHECK);
      if(failedChecks && failedChecks.length > 0  && !(skipValidation())){
        try{
          _postDeviceMetrics(failedChecks);
        }
        catch(err){
          console.error("[DeviceMetrics] sending the device metrics failed.")
        }
      if(failedChecks[0].actionOnFailure !== "DeviceValidationFailure")dispatch(createFailureAction(failedChecks[0].actionOnFailure));
      else {dispatch(createSetViewAction("DeviceValidationFailureView"))}
      resolve(false);
     }
     else {
      try{
        _postDeviceMetrics();
      }
      catch(err){
        console.error("[DeviceMetrics] sending the device metrics failed.")
      }
  
      state.sessionState.appAccessedFor === "Assess" ? dispatch(createSetConfirmationModalAction(true)): dispatch(createSetViewAction("DeviceValidationSuccessView"));
      resolve(true);
     }
    });
    })
  }

  return {postDeviceValidationLog,performDeviceValidation,postLocationDeviceMetrics,postAllValidationLogsFromIndexDB,getSummary,performScreenResolutionCheck,performDuplicateScreenCheck,validateVitalsChecks};
}
